<template>
  <div class="card shadow-sm">
    <div class="card-body">

      <div class="table-responsive">
        <table class="index-marker-table">
          <thead>
          <tr class="main-header">
            <th class="category-bottom" colspan="2">{{ $t('qvct_survey.surveyReport.identifiedFields') }}</th>
            <th v-if="selectedPopulation === 'true'" :colspan="populationGroups.length">{{ $t('qvct_survey.surveyReport.satisfactionLevels') }}</th>
            <th v-else>{{ $t('qvct_survey.surveyReport.satisfactionLevels') }}</th>
            <th v-if="selectedPopulation === 'true'" rowspan="2">{{ $t('qvct_survey.surveyReport.problematicAreas') }}</th>
            <th rowspan="2">{{ $t('qvct_survey.surveyReport.observations') }}</th>
          </tr>
          <tr class="main-header">
            <th class="category1"></th>
            <th class="category2"></th>
            <template v-if="selectedPopulation === 'true'">
              <th v-for="popId in populationGroups" :key="popId" :title="getPopulationAcronym(popId)">
                {{ getPopulationAcronym(popId) }}
              </th>
            </template>
            <template v-else>
              <th title="TPC">TPC</th>
            </template>
          </tr>
          </thead>
          <tbody>
          <template v-for="category in tableData" :key="category.name">
            <tr v-if="(category.subCategories || []).length !== 0">
              <td class="category-header" :rowspan="category.rowspan">{{ category.name }}</td>
            </tr>

  
            <tr v-if="(category.subCategories || []).length === 0">
             <td class="rowstep"></td>
             <td class="rowstep1" rowspan="1" style="font-weight: bold;">{{ category.name }}</td>
              <template v-if="selectedPopulation === 'true'">
                <td v-for="popId in populationGroups" :key="popId"
                    class="satisfaction-cell"
                    :class="getResponseMarkClass(category.populationsData?.[popId]?.reponse_mark)"
                    :style="{
                        backgroundColor: category.populationsData?.[popId]?.color || 'transparent',
                        color: category.populationsData?.[popId]?.color ? '#ffffff' : '#333333',
                        fontWeight: 'bold',
                        fontSize: '12px'
                      }">
                  {{ category.populationsData?.[popId]?.average_percent ? category.populationsData[popId].average_percent + '%' : '0%' }}
                </td>
              </template>
              <template v-else>
                <td
                    class="satisfaction-cell"
                    :class="getResponseMarkClass(category.allPopData?.reponse_mark)"
                    :style="{
                        backgroundColor: category.allPopData?.color || 'transparent',
                        color: category.allPopData?.color ? '#ffffff' : '#333333',
                        fontWeight: 'bold',
                        fontSize: '12px'
                      }">
                  {{ category.allPopData?.average_percent ? category.allPopData.average_percent + '%' : '0%' }}
                </td>
              </template>

              <td v-if="selectedPopulation === 'true'" class="problematic-cell">
                <span v-for="(popId, index) in populationGroups" :key="popId">
                  <span v-if="category.populationsData?.[popId]?.reponse_mark === 'Stress'">
                    -{{ getPopulationAcronym(popId) }}{{ index < populationGroups.length - 1 ? ' ' : '' }}
                  </span>
                </span>
              </td>
              <td class="problematic-cell">
                1  2 3 4 5 6 7 8 9 10
              </td>
            </tr>

            <tr v-for="subCategory in category.subCategories" :key="subCategory.name">
              <td class="subcategory">{{ subCategory.name }}</td>

              <template v-if="selectedPopulation === 'true'">
                <td v-for="popId in populationGroups" :key="popId"
                    class="satisfaction-cell"
                    :class="getResponseMarkClass(subCategory.populationsData[popId]?.reponse_mark)"
                    :style="{
                        backgroundColor: subCategory.populationsData[popId]?.color || 'transparent',
                        color: subCategory.populationsData[popId]?.color ? '#ffffff' : '#333333',
                        fontWeight: 'bold',
                        fontSize: '12px'
                      }">
                  {{ subCategory.populationsData[popId]?.average_percent ? subCategory.populationsData[popId].average_percent + '%' : '0%' }}
                </td>
              </template>
              <template v-else>
                <td
                    class="satisfaction-cell"
                    :class="getResponseMarkClass(subCategory.allPopData?.reponse_mark)"
                    :style="{
                        backgroundColor: subCategory.allPopData?.color || 'transparent',
                        color: subCategory.allPopData?.color ? '#ffffff' : '#333333',
                        fontWeight: 'bold',
                        fontSize: '12px'
                      }">
                  {{ subCategory.allPopData?.average_percent ? subCategory.allPopData.average_percent + '%' : '0%' }}
                </td>
              </template>

              <td v-if="selectedPopulation === 'true'" class="problematic-cell">
                <span v-for="(popId, index) in populationGroups" :key="popId">
                  <span v-if="subCategory.populationsData[popId]?.reponse_mark === 'Stress'">
                    -{{ getPopulationAcronym(popId) }}{{ index < populationGroups.length - 1 ? ' ' : '' }}
                  </span>
                </span>
              </td>
              <td class="problematic-cell">
                1  2 3 4 5 6 7 8 9 10
              </td>
            </tr>
          </template>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  surveyReport: {
    type: Object,
    required: true
  },
  selectedCategory: {
    type: String,
    required: true
  },
  selectedPopulation: {
    type: String,
    required: true
  }
})

// Get population groups
const populationGroups = computed(() => {
  if (!props.surveyReport) return []
  return Object.keys(props.surveyReport).filter(key => key !== 'all_populations')
})

// Helpers to traverse arbitrary depths (collect and resolve by path)
const collectLeafPaths = (node, currentPath = [], out = []) => {
  if (!node || typeof node !== 'object') return out
  const subs = node.sub_categories || {}
  const keys = Object.keys(subs)
  if (keys.length === 0) return out
  keys.forEach(key => {
    const child = subs[key]
    const childSubs = child?.sub_categories || {}
    if (Object.keys(childSubs).length === 0) {
      out.push([...currentPath, key])
    } else {
      collectLeafPaths(child, [...currentPath, key], out)
    }
  })
  return out
}

const findNodeByPath = (node, pathArray = []) => {
  if (!node || typeof node !== 'object') return null
  let cursor = node
  for (const segment of pathArray) {
    const subs = cursor?.sub_categories || {}
    if (!subs[segment]) return null
    cursor = subs[segment]
  }
  return cursor || null
}

// Build All-populations (TPC) data accessor
const getAllPopCategory = (categoryName) => {
  const cat = props.surveyReport?.all_populations?.types?.[props.selectedCategory]?.sub_categories?.[categoryName]
  return cat || null
}

const getAllPopSubCat = (categoryName, subCatName) => {
  const sub = props.surveyReport?.all_populations?.types?.[props.selectedCategory]?.sub_categories?.[categoryName]?.sub_categories?.[subCatName]
  return sub || null
}

// Get population acronym
const getPopulationAcronym = (popId) => {
  return props.surveyReport[popId]?.acronym || popId
}

// Get response mark CSS class
const getResponseMarkClass = (reponseMark) => {
  if (!reponseMark) return ''
  return reponseMark.toLowerCase()
}

// Build table data structure
const tableData = computed(() => {
  if (!props.surveyReport || !props.selectedCategory) return []
  
  const data = []
  
  if (props.selectedPopulation === 'true') {
    // Build a unified structure across all populations
    const categoriesMap = {}
    populationGroups.value.forEach(popId => {
      const catRoot = props.surveyReport[popId]?.types?.[props.selectedCategory]?.sub_categories || {}
      Object.entries(catRoot).forEach(([categoryName, category]) => {
        if (!categoriesMap[categoryName]) categoriesMap[categoryName] = new Set()
        const leafPaths = collectLeafPaths(category, [])
        leafPaths.forEach(p => categoriesMap[categoryName].add(JSON.stringify(p)))
      })
    })

    const categoryNames = Object.keys(categoriesMap)
    if (categoryNames.length === 0) return []

    categoryNames.forEach(categoryName => {
      const categoryRow = {
        name: categoryName,
        rowspan: 1,
        populationsData: {},
        subCategories: []
      }

      // Populate main category row data per population
      populationGroups.value.forEach(popId => {
        const popCategory = props.surveyReport[popId]?.types?.[props.selectedCategory]?.sub_categories?.[categoryName]
        categoryRow.populationsData[popId] = popCategory || null
      })

      // Build subcategory rows from union of leaf paths across populations
      const unionLeafPaths = Array.from(categoriesMap[categoryName] || [])
      unionLeafPaths.forEach(pathJson => {
        const path = JSON.parse(pathJson)
        const label = path[path.length - 1]
        const subCategoryRow = { name: label, populationsData: {} }
        populationGroups.value.forEach(popId => {
          const popCategoryNode = props.surveyReport[popId]?.types?.[props.selectedCategory]?.sub_categories?.[categoryName]
          const popSubCat = findNodeByPath(popCategoryNode, path)
          subCategoryRow.populationsData[popId] = popSubCat || null
        })
        categoryRow.subCategories.push(subCategoryRow)
      })

      categoryRow.rowspan = 1 + categoryRow.subCategories.length
      data.push(categoryRow)
    })
  } else {
    // All populations (TPC) mode) — support varying depths (category only, category -> subcat, category -> subcat -> subsubcat, ...)
    const aggregatedCategory = props.surveyReport?.all_populations?.types?.[props.selectedCategory]
    if (!aggregatedCategory?.sub_categories) return []

    Object.entries(aggregatedCategory.sub_categories).forEach(([categoryName, category]) => {
      const categoryRow = {
        name: categoryName,
        rowspan: 1,
        allPopData: getAllPopCategory(categoryName),
        subCategories: []
      }

      const subCats = category?.sub_categories || {}
      const subCatNames = Object.keys(subCats)

      // If no sub-categories, still render the category row with its aggregated cell
      if (subCatNames.length === 0) {
        categoryRow.rowspan = 1
        data.push(categoryRow)
        return
      }

      // Collect all leaf paths under this category (arbitrary depth)
      const leafPaths = collectLeafPaths(category, [])

      // If there are no leaves (shouldn't happen if subCats exist), fallback to first-level names
      if (leafPaths.length === 0) {
        subCatNames.forEach((firstLevel) => {
          if (!firstLevel || firstLevel.trim() === '') return
          const leafNode = category?.sub_categories?.[firstLevel] || null
          categoryRow.subCategories.push({ name: firstLevel, allPopData: leafNode })
        })
      } else {
        leafPaths.forEach((path) => {
          const label = path[path.length - 1]
          const leafNode = findNodeByPath(category, path)
          categoryRow.subCategories.push({ name: label, allPopData: leafNode || null })
        })
      }

      categoryRow.rowspan = 1 + categoryRow.subCategories.length
      data.push(categoryRow)
    })
  }
  
  return data
})
</script>

<style scoped>
.index-marker-table {
  width: 100%;
  border-collapse: collapse;
  margin: 10px 0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  font-size: 9px;
}

.index-marker-table th,
.index-marker-table td {
  border: 1px solid #bdc3c7;
  padding: 4px 3px;
  vertical-align: middle;
}

.index-marker-table th {
  background-color: #ecf0f1;
  font-weight: bold;
  color: #2c3e50;
  text-align: center;
}

.main-header {
  background-color: #3498db;
  color: white;
  font-size: 9px;
}

.category-header {
  background-color: #eaf2f8;
  font-weight: bold;
  text-align: left;
  font-size: 9px;
}

.subcategory {
  text-align: left;
  background-color: #f8f9fa;
  padding-left: 15px !important;
  font-size: 9px;
}

.satisfaction-cell {
  min-width: 20px;
  text-align: center;
  position: relative;
  font-size: 8px;
  padding: 2px;
}

.problematic-cell {
  min-width: 80px;
  text-align: left;
  padding-left: 5px;
  font-size: 8px;
}

.category1 {
  border-bottom: none !important;
  border-top: none !important;
  border-right: none !important;
}

.category2 {
  border-bottom: none !important;
  border-top: none !important;
  border-left: none !important;
}

.category-bottom {
  border-bottom: none !important;
}

.rowstep{
  border-right: none !important;
}

.rowstep1{
  border-left: none !important;
}
/* Response mark color classes */
.stress {
  color: #ffffff !important;
  font-weight: bold;
}

.satisfaction {
  color: #ffffff !important;
  font-weight: bold;
}

.neutre {
  color: #333333 !important;
  font-weight: bold;
}

@media (max-width: 768px) {
  .index-marker-table {
    font-size: 8px;
  }
  
  .index-marker-table th,
  .index-marker-table td {
    padding: 2px 1px;
  }
}
</style>
